"use client";

/**
 * Accept-only cookie-consent notice. Copy is admin-editable (markdown) via
 * Site Settings → Communication and arrives through the site-status
 * snapshot. Acceptance is recorded in a long-lived cookie so the notice
 * stays dismissed across reloads and navigation. No analytics gating —
 * this is a notice, not a consent gate (see issue #794).
 */

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const CONSENT_COOKIE = "rawiah.cookie_consent";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function hasConsent(): boolean {
  return document.cookie
    .split("; ")
    .some((c) => c.startsWith(`${CONSENT_COOKIE}=`));
}

export function CookieConsent({
  banner,
}: {
  banner: { enabled: boolean; message: string };
}) {
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setAccepted(hasConsent());
  }, []);

  // Avoid SSR/CSR mismatch — the consent cookie can only be read client-side.
  if (!mounted) return null;
  if (!banner.enabled || !banner.message) return null;
  if (accepted) return null;
  // The live meeting room is kept clear of site-wide chrome (matches the
  // announcement banner) — the call UI owns the viewport there.
  if (pathname === "/meeting" || pathname.startsWith("/meeting/")) return null;

  const accept = () => {
    document.cookie = `${CONSENT_COOKIE}=1; path=/; max-age=${ONE_YEAR_SECONDS}; SameSite=Lax`;
    setAccepted(true);
  };

  return (
    <div
      data-testid="cookieConsent"
      role="dialog"
      aria-label="Cookie notice"
      className="fixed inset-x-0 bottom-0 z-[9997] border-t border-white/10 bg-[#1c2746] px-4 py-3 text-white shadow-2xl sm:px-6"
    >
      <div className="mx-auto flex max-w-5xl flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div
          data-testid="cookieConsentMessage"
          className="text-sm leading-relaxed text-white/90 [&_a]:font-medium [&_a]:text-[#F6C65A] [&_a]:underline [&_strong]:font-semibold"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Markdown renders as inline copy — collapse paragraphs so the
              // notice stays on one line where it fits.
              p: ({ children }) => <span>{children}</span>,
              a: ({ href, children }) => (
                <a
                  href={href}
                  target={href?.startsWith("http") ? "_blank" : undefined}
                  rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
                >
                  {children}
                </a>
              ),
            }}
          >
            {banner.message}
          </ReactMarkdown>
        </div>
        <button
          type="button"
          onClick={accept}
          data-testid="cookieConsentAccept"
          className="shrink-0 cursor-pointer rounded-md bg-white px-4 py-2 text-sm font-semibold text-[#27335D] transition-colors hover:bg-white/90"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
